import type { OfflineDbExecutor } from './driver-types';

/**
 * Small typed accessors over the `meta` key/value table: cache ownership,
 * the offline-readability clock, and the feature-gate snapshot. Everything
 * here is what makes the cache SAFE rather than what makes it useful - the
 * repositories carry the data, this table carries the rules.
 */
const OWNER_KEY = 'owner_user_id';
const VERIFIED_AT_KEY = 'last_verified_at';
const GATE_KEY_PREFIX = 'gate_enabled:';
const MAX_SEEN_NOW_KEY = 'max_seen_now';
const ENTRY_COMPLETE_SESSION_ID_KEY = 'entry_complete_session_id';

/**
 * The kill switch is evaluated PER ORGANISATION on the server, so its cached
 * answer is per organisation here too. A single device-wide flag let an org
 * whose gate was still on overwrite the "off" recorded for a different org,
 * re-opening the cache for rows the switch was flipped to shut.
 */
function gateKey(organizationId: string): string {
  return `${GATE_KEY_PREFIX}${organizationId}`;
}

/**
 * Offline readability bound (R15): reads lock after this long without a
 * successful online session + membership re-verification. 30 days - the
 * loose end of the ISMS range, per the owner's "must not limit" direction
 * recorded in the plan.
 */
export const OFFLINE_READ_LOCK_MS = 30 * 24 * 60 * 60 * 1000;

async function getMetaValue(driver: OfflineDbExecutor, key: string): Promise<string | null> {
  const rows = await driver.query<{ value: string }>('SELECT value FROM meta WHERE key = ?', [key]);
  return rows[0]?.value ?? null;
}

async function setMetaValue(driver: OfflineDbExecutor, key: string, value: string): Promise<void> {
  await driver.run(
    'INSERT INTO meta (key, value) VALUES (?, ?) ON CONFLICT(key) DO UPDATE SET value = excluded.value',
    [key, value],
  );
}

async function deleteMetaValue(driver: OfflineDbExecutor, key: string): Promise<void> {
  await driver.run('DELETE FROM meta WHERE key = ?', [key]);
}

export async function getCacheOwner(driver: OfflineDbExecutor): Promise<string | null> {
  return await getMetaValue(driver, OWNER_KEY);
}

export async function setCacheOwner(driver: OfflineDbExecutor, userId: string): Promise<void> {
  await setMetaValue(driver, OWNER_KEY, userId);
}

/**
 * Monotonic clock ratchet: R15 is defeated by winding the device clock back
 * (Settings > Date & Time, no jailbreak needed) unless the cache remembers
 * the latest wall-clock it has ever observed and measures against THAT.
 * Time only moves forward here; a rolled-back clock can shorten the read
 * window (fail-closed), never extend it.
 */
async function ratchetClock(driver: OfflineDbExecutor, nowMs: number): Promise<number> {
  const stored = await getMetaValue(driver, MAX_SEEN_NOW_KEY);
  const parsed = stored === null ? 0 : Number(stored);
  const maxSeen = Number.isFinite(parsed) ? parsed : 0;
  if (nowMs > maxSeen) {
    await setMetaValue(driver, MAX_SEEN_NOW_KEY, String(nowMs));
    return nowMs;
  }
  return maxSeen;
}

/**
 * Stamp a successful online session + membership verification. The sync
 * layer calls this; its absence for {@link OFFLINE_READ_LOCK_MS} locks reads.
 *
 * `verifiedAtMs` MUST be SERVER-anchored, never a bare `Date.now()`. Winding
 * the device clock a year forward (Settings > Date & Time), completing one
 * online pass and winding it back would otherwise write a stamp a year in the
 * future - and since the ratchet below would have recorded that same future
 * value, `isReadAllowed` measures zero elapsed time against it and R15 is
 * defeated for as long as the forward jump was.
 *
 * The stamp REBASES the ratchet rather than feeding it, and this is the one
 * moment where lowering it is sound: an authenticated server answer is the
 * only authority on real time this device ever gets, so a `max_seen_now`
 * poisoned forward by an earlier clock excursion is corrected here instead of
 * read-locking a healthy device until wall-clock caught up to it. Between
 * verifications the ratchet still only moves forward.
 */
export async function markVerified(driver: OfflineDbExecutor, verifiedAtMs: number): Promise<void> {
  await setMetaValue(driver, MAX_SEEN_NOW_KEY, String(verifiedAtMs));
  await setMetaValue(driver, VERIFIED_AT_KEY, String(verifiedAtMs));
}

/**
 * Drop the verification stamp, locking offline reads until the next proven
 * pass re-stamps.
 *
 * The durable barrier behind a revocation purge: the purge deletes rows, but a
 * write-through authorised moments before the revocation can still land after
 * it and repopulate them. Without the stamp those rows are unreadable (both
 * here and in the executor's payload gate), so the race costs disk, not data.
 */
export async function clearVerified(driver: OfflineDbExecutor): Promise<void> {
  await deleteMetaValue(driver, VERIFIED_AT_KEY);
}

/**
 * Whether offline reads are still allowed. A cache with NO verification
 * stamp is locked - a fresh stamp is part of coming online, so an unstamped
 * cache is one that never completed a verified sync. Evaluated against the
 * ratcheted clock, so rolling the device clock back cannot reopen a window
 * that has already closed.
 */
export async function isReadAllowed(driver: OfflineDbExecutor, nowMs: number): Promise<boolean> {
  const effectiveNow = await ratchetClock(driver, nowMs);
  const stamp = await getMetaValue(driver, VERIFIED_AT_KEY);
  if (stamp === null) {
    return false;
  }
  const verifiedAt = Number(stamp);
  return Number.isFinite(verifiedAt) && effectiveNow - verifiedAt < OFFLINE_READ_LOCK_MS;
}

/**
 * Last-known feature-gate value FOR ONE ORGANISATION, honoured offline (the
 * gate is server data an offline cold start cannot evaluate). Absent =
 * enabled: the gate ships as an emergency kill-switch, on by default, per the
 * recorded owner decision - and a gate FLIP to off purges that org's rows at
 * the next online evaluation, so absence never masks a deliberate off.
 */
export async function isGateEnabled(
  driver: OfflineDbExecutor,
  organizationId: string,
): Promise<boolean> {
  const value = await getMetaValue(driver, gateKey(organizationId));
  return value !== 'false';
}

export async function setGateEnabled(
  driver: OfflineDbExecutor,
  organizationId: string,
  enabled: boolean,
): Promise<void> {
  await setMetaValue(driver, gateKey(organizationId), enabled ? 'true' : 'false');
}

/** Record the stable session identity only after app-entry MFA policy resolved determinately. */
export async function markEntrySessionComplete(
  driver: OfflineDbExecutor,
  sessionId: string,
): Promise<void> {
  await setMetaValue(driver, ENTRY_COMPLETE_SESSION_ID_KEY, sessionId);
}

/** A new login session must never inherit an older session's completed-entry decision. */
export async function isEntrySessionComplete(
  driver: OfflineDbExecutor,
  sessionId: string,
): Promise<boolean> {
  return (await getMetaValue(driver, ENTRY_COMPLETE_SESSION_ID_KEY)) === sessionId;
}

/** Revoke only the matching session proof so an older route cannot clear a newer login. */
export async function clearEntrySessionComplete(
  driver: OfflineDbExecutor,
  sessionId: string,
): Promise<void> {
  if ((await getMetaValue(driver, ENTRY_COMPLETE_SESSION_ID_KEY)) === sessionId) {
    await deleteMetaValue(driver, ENTRY_COMPLETE_SESSION_ID_KEY);
  }
}
